import {
  default as ButtonPrimary,
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  Button,
  TextField,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import * as systemActions from "@rentiohq/web-shared/dist/redux/system/system.actions";
import * as systemSelectors from "@rentiohq/web-shared/dist/redux/system/system.selectors";
import { EPreferencePersistScope } from "@rentiohq/web-shared/dist/redux/system/system.types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import * as CONFIG from "../../TenantProfileNotificationPreferences.config";
import * as S from "../../TenantProfileNotificationPreferences.styles";
import {
  checkSms,
  getSmsExampleValues,
  getSmsTextByKey,
  ISmsCheck,
  SMS_SEGMENT_LENGTH,
  toPlainSmsCharacters,
} from "./VisitReminderSmsTexts.utils";

const LANGUAGES = [
  {
    label: "nl",
    locale: ELocale.NL,
    key: CONFIG.PREFERENCE_VISIT_REMINDER_SMS_NL,
  },
  {
    label: "fr",
    locale: ELocale.FR,
    key: CONFIG.PREFERENCE_VISIT_REMINDER_SMS_FR,
  },
  {
    label: "en",
    locale: ELocale.EN,
    key: CONFIG.PREFERENCE_VISIT_REMINDER_SMS_EN,
  },
];

/** Why a text cannot be saved, one line per reason, or nothing when it can. */
const getProblems = (check: ISmsCheck) => {
  const problems: string[] = [];

  if (check.missingPlaceholders.length > 0) {
    problems.push(
      getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.error.missing",
        {
          placeholders: check.missingPlaceholders
            .map(name => `{{${name}}}`)
            .join(", "),
        },
      ),
    );
  }

  if (check.unknownPlaceholders.length > 0) {
    problems.push(
      getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.error.unknown",
        {
          placeholders: check.unknownPlaceholders
            .map(name => `{{${name}}}`)
            .join(", "),
        },
      ),
    );
  }

  if (check.forbiddenCharacters.length > 0) {
    problems.push(
      getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.error.characters",
        { characters: check.forbiddenCharacters.join(" ") },
      ),
    );
  }

  if (check.length > SMS_SEGMENT_LENGTH) {
    problems.push(
      getLocalizedText(
        "tenant_profile.notification.visit_reminder_sms.error.too_long",
      ),
    );
  }

  return problems;
};

/**
 * How many SMS the text is billed as, and what that costs when the price is
 * known: "3 sms'en, € 0,45" (DEV-3572). Literal keys, so the i18n key check
 * sees both.
 */
const getPartsLine = (segments: number, smsPrice?: number) => {
  const parts =
    segments === 1
      ? getLocalizedText(
          "tenant_profile.notification.visit_reminder_sms.parts.one",
        )
      : getLocalizedText(
          "tenant_profile.notification.visit_reminder_sms.parts.many",
          { count: `${segments}` },
        );

  return smsPrice === undefined
    ? parts
    : `${parts}, ${formatCurrency(smsPrice * segments)}`;
};

interface IProps {
  /** What one SMS costs the office excl. VAT, undefined when unknown. */
  smsPrice?: number;
}

/**
 * The office's own visit reminder SMS, one plain-text box per language, under
 * the switch that turns the SMS on (DEV-3553, Jonas 2026-10-28).
 *
 * Every box shows the SMS as the example candidate reads it and counts that,
 * not the raw placeholders, so "160" means what the candidate gets. A text
 * that runs over, leaves out the address, date or time, or holds something no
 * SMS can carry is refused on save, per box.
 *
 * Next to the count, how many SMS the text is billed as: one per part
 * (DEV-3572), counted with the same rule as the billing in serverless.
 */
const VisitReminderSmsTexts = (props: IProps) => {
  const { smsPrice } = props;

  const dispatch = useDispatch();

  // The texts belong to the switch above: while the office does not send the
  // SMS there is nothing to write. Off unless stored as on, like the sender.
  const isEnabled = useSelector(
    (state: IRootStore) =>
      systemSelectors.getPreference<boolean>(
        state,
        CONFIG.PREFERENCE_VISIT_REMINDER_SMS_ENABLED,
      ) === true,
  );

  const defaultByKey: { [key: string]: string } = {};
  LANGUAGES.forEach(({ key, locale }) => {
    defaultByKey[key] = getLocalizedText(
      "tenant_profile.notification.visit_reminder_sms.placeholder",
      undefined,
      undefined,
      locale,
    );
  });

  const stored = useSelector((state: IRootStore) =>
    systemSelectors.getPreferences(
      state,
      LANGUAGES.map(({ key }) => ({ key, defaultValue: defaultByKey[key] })),
    ),
  );

  // Only what the office typed lives in state; every other box reads the
  // stored text on each render. Seeding all boxes once would freeze the
  // defaults when the preferences arrive after mount, and Save, which writes
  // every language, would then overwrite the office's own texts with them.
  const [editedTextByKey, setEditedTextByKey] = React.useState<{
    [key: string]: string;
  }>({});
  const textByKey = getSmsTextByKey({ stored, edited: editedTextByKey });
  const [showProblems, setShowProblems] = React.useState(false);

  const exampleValues = getSmsExampleValues();

  const setText = (key: string, text: string) =>
    setEditedTextByKey(previous => ({
      ...previous,
      [key]: toPlainSmsCharacters(text),
    }));

  const handlePressSave = () => {
    const isValid = LANGUAGES.every(
      ({ key }) => checkSms({ text: textByKey[key], exampleValues }).canSave,
    );

    if (!isValid) {
      setShowProblems(true);
      showAlert({
        type: "error",
        message: getLocalizedText(
          "tenant_profile.notification.visit_reminder_sms.error.not_saved",
        ),
      });
      return;
    }

    // A text equal to the default is cleared rather than stored, so the
    // office keeps getting the standard copy, and any later change to it.
    const preferences: { [key: string]: string | null } = {};
    LANGUAGES.forEach(({ key }) => {
      const text = textByKey[key].trim();
      preferences[key] = text === defaultByKey[key].trim() ? null : text;
    });

    dispatch(
      systemActions.setPreferences({
        persistScope: EPreferencePersistScope.RemoteBroker,
        preferences,
      }),
    );

    setShowProblems(false);
    showAlert({
      type: "success",
      message: getLocalizedText("preference.updated.alert.title"),
    });
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <div style={{ maxWidth: "600px" }}>
      {LANGUAGES.map(({ key, label }, index) => {
        const text = textByKey[key];
        const check = checkSms({ text, exampleValues });
        const problems = getProblems(check);

        return (
          <React.Fragment key={key}>
            {index > 0 ? (
              <Spacer weight={ESpacerWeight.W32} />
            ) : (
              <Spacer weight={ESpacerWeight.W16} />
            )}

            <TextField
              id={key}
              name={key}
              multiline={true}
              label={getLocalizedText(
                `tenant_profile.notification.visit_reminder_sms.label.${label}`,
              )}
              value={text}
              error={
                showProblems && problems.length > 0
                  ? problems.join(" ")
                  : undefined
              }
              onChange={(
                event: React.ChangeEvent<
                  HTMLInputElement | HTMLTextAreaElement
                >,
              ) => setText(key, event.target.value)}
            />

            <Spacer weight={ESpacerWeight.W04} />

            <TextStyle
              variation={
                check.length > SMS_SEGMENT_LENGTH ? "negative" : "subdued"
              }
              size="small"
            >
              {`${getLocalizedText(
                "tenant_profile.notification.visit_reminder_sms.counter",
                {
                  length: `${check.length}`,
                  limit: `${SMS_SEGMENT_LENGTH}`,
                },
              )} · ${getPartsLine(check.segments, smsPrice)}`}
            </TextStyle>

            {check.longSmsCharacters.length > 0 && (
              <div>
                <TextStyle variation="warn" size="small">
                  {getLocalizedText(
                    "tenant_profile.notification.visit_reminder_sms.long_sms",
                    {
                      characters: check.longSmsCharacters.join(" "),
                      segments: `${check.segments}`,
                    },
                  )}
                </TextStyle>
              </div>
            )}

            {!showProblems && problems.length > 0 && (
              <div>
                <TextStyle variation="negative" size="small">
                  {problems.join(" ")}
                </TextStyle>
              </div>
            )}

            <Spacer weight={ESpacerWeight.W08} />

            <TextStyle variation="subdued" size="small">
              {getLocalizedText(
                "tenant_profile.notification.visit_reminder_sms.preview",
              )}
            </TextStyle>
            <S.SmsPreview>{check.preview}</S.SmsPreview>

            <div style={{ marginTop: "4px" }}>
              <Button
                appearance="ghost"
                size="slim"
                onClick={() => setText(key, defaultByKey[key])}
              >
                {getLocalizedText("system.reset")}
              </Button>
            </div>
          </React.Fragment>
        );
      })}

      <Spacer weight={ESpacerWeight.W24} />

      <ButtonPrimary
        title={getLocalizedText("system.save")}
        onPress={handlePressSave}
        buttonSize={EButtonSize.Medium}
      />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default VisitReminderSmsTexts;
